import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GeoMapItem, Sensor } from '@models';
import { SensorReadingInfo } from '@models/EventLog/SensorReadingInfo.model';
import { ImageLabelType } from '@models/ImageFavorite/imageFavorite.enums';
import { ImageLabelProperties } from '@models/ImageFavorite/imageLabelProperties.model';
import { ImageLabelValue } from '@models/ImageFavorite/imageLabelValue.model';
import { MapDetails, MapPopupState } from '@models/MapFavorite/mapDetails.model';
import { MapLabelInfo } from '@models/MapFavorite/mapLabelInfo.model';
import { MapFavoriteService } from '@services/mapFavorite.service';
import { Charting } from 'app/shared/classes/charting';
import { CommonShared } from 'app/shared/classes/common';
import { arrowColor, formatLabelValue, LABEL_BASE_WIDTH_PX, labelBoxStyle, labelFontScale, labelTypeHasArrow, labelValueStyle, labelValueTooltip, newLabelProps, parseLabelProps } from 'app/shared/classes/labelStyling';
import { ImageLabelEditorModalComponent } from '../image-favorite/image-label-editor-modal/image-label-editor-modal.component';

// One label, ready to render: positions are pixels in the map's overlay pane.
interface LabelView {
  label: MapLabelInfo;
  style: { [key: string]: any };
  valueStyle: { [key: string]: any };
  valueText: string;
  valueTooltip: string;
  isRegion: boolean;
  isPlain: boolean;
  // Arrow-tip handle (edit mode), when the label has an arrow.
  target: { x: number; y: number } | null;
}

interface LabelArrow {
  x1: number; y1: number; x2: number; y2: number; color: string;
}

interface LabelDrag {
  label: MapLabelInfo;
  mode: 'box' | 'target' | 'resize';
  startX: number;
  startY: number;
  anchor: { x: number; y: number };
  se: { x: number; y: number } | null;
  offsetX: number;
  offsetY: number;
  moved: boolean;
}

// A map favorite: the readings of the selected sensors plotted as pins (one per sensor
// and position), pin popups that can be opened, dragged and charted, and labels pinned
// to map locations. It does not load data: the host passes the sensors and readings and
// restores / captures the view through savedMapDetails / mapDetailsChanged.
//
// Hosts must resolve each reading's gpsLocation first (see backfillGpsLocations) and pass
// only the readings of the selected reading types.
@Component({
  standalone: false,
  selector: 'kig-map-favorite',
  templateUrl: './map-favorite.component.html',
  styleUrls: ['./map-favorite.component.scss']
})
export class MapFavoriteComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() sensors: Sensor[] = [];
  @Input() sensorReadings: SensorReadingInfo[] = [];

  // The view and popups to restore. Set it in the same change as the readings it belongs to.
  @Input() savedMapDetails: MapDetails | null = null;

  // Labels: loaded from / saved to the server for a saved favorite (mapFavoriteId > 0), or
  // held by the host in draft mode (the dashboard wizard). In draft mode the labels are
  // local: edits change that array and are emitted through draftLabelsChange instead of
  // being saved (new labels get negative temporary ids), and values come from the
  // preview endpoint.
  @Input() mapFavoriteId = 0;
  @Input() draftLabels: MapLabelInfo[] | null = null;
  @Input() labelsEditable = false;
  @Input() username = 'unknown';
  @Input() refreshSeconds = 300;

  // CSS height of the component; the default fills the host.
  @Input() height = '100%';
  @Input() minimal = false;

  @Output() mapDetailsChanged = new EventEmitter<MapDetails>();
  @Output() labelsEditModeChange = new EventEmitter<boolean>();
  @Output() draftLabelsChange = new EventEmitter<MapLabelInfo[]>();

  // Makes the SVG arrow-marker ids unique on pages that show several maps.
  private static nextInstanceId = 0;
  readonly instanceId = ++MapFavoriteComponent.nextInstanceId;

  @ViewChild(GoogleMap) map: GoogleMap;
  @ViewChildren(MapMarker) markers: QueryList<MapMarker>;
  @ViewChildren(MapInfoWindow) infoWindows: QueryList<MapInfoWindow>;
  // static: the map wrapper can emit mapInitialized synchronously while this view is
  // still being created, before non-static queries are resolved.
  @ViewChild('labelLayer', { static: true }) labelLayerRef: ElementRef<HTMLDivElement>;

  common = new CommonShared();
  charting = new Charting();
  readonly LabelType = ImageLabelType;

  // ---- map ----
  initialCenter = { lat: 39.30, lng: -97.90 };
  initialZoom = 3;
  mapTypeId = 'satellite';   // 'satellite' | 'roadmap' | 'hybrid' | 'terrain'
  geoMapItems: GeoMapItem[] = [];

  // ---- pin popup charts ----
  chartOptions: any = {
    showXAxis: true,
    showYAxis: true,
    gradient: false,
    showLegend: false,
    showXAxisLabel: false,
    xAxisLabel: 'Date-Time',
    showYAxisLabel: false,
    yAxisLabel: 'Value',
    axisFormat: null,
    autoScale: true,
    scaleYMin: null,
    scaleYMax: null,
    legendTitle: 'Sensors & Reading Types',
    timeline: false,
    colorScheme: {
      domain: [ '#046dd6', '#800080', '#42f55a', '#ffc107', '#fa0f0f' ]
    }
  };
  view: any[] = [255, 160];

  // ---- labels ----
  labels: MapLabelInfo[] = [];
  labelViews: LabelView[] = [];
  labelArrows: LabelArrow[] = [];
  labelsEditMode = false;

  private labelValues = new Map<number, ImageLabelValue>();
  private boxSizes = new Map<number, { w: number; h: number }>();
  private labelOverlay: any = null;
  private resizeObserver: ResizeObserver | null = null;

  // Width of the map container, so label fonts (and the pixel offsets / widths stored with a label)
  // scale the same way image favorite labels do. Until the first measurement the scale is 1.
  private mapWidthPx = LABEL_BASE_WIDTH_PX;
  private labelDrag: LabelDrag | null = null;
  private labelEditorRef: BsModalRef | null = null;
  private refreshSub: Subscription | null = null;
  private destroy$ = new Subject<void>();

  // ---- view / popup restore bookkeeping ----
  private mapReady = false;
  private nativeMap: google.maps.Map | undefined;
  private pendingDetails: MapDetails | null = null;   // saved view + popups not applied yet
  private viewToApply: MapDetails | null = null;
  private skipNextFit = false;
  private lastPinSignature = '';
  private restoring = false;
  private restoreToken = 0;
  private lastKnownView: { center: { lat: number; lng: number }; zoom: number; mapTypeId: string } | null = null;

  constructor(private mapFavoriteService: MapFavoriteService,
              private modalService: BsModalService,
              private toastr: ToastrService,
              private zone: NgZone,
              private hostRef: ElementRef<HTMLElement>) {}


  // ---------------------------------------------------------------- lifecycle

  get isDraft(): boolean {
    return this.draftLabels != null;
  }

  // Labels come from somewhere: the host (draft) or a saved favorite.
  get hasLabelSource(): boolean {
    return this.isDraft || this.mapFavoriteId > 0;
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['savedMapDetails'] != undefined && this.savedMapDetails != undefined) {
      this.pendingDetails = this.savedMapDetails;
      this.viewToApply = this.savedMapDetails;
      this.applyPendingView();
    }

    if (changes['sensors'] != undefined || changes['sensorReadings'] != undefined || changes['savedMapDetails'] != undefined) {
      this.rebuildPins();
    }

    if (changes['mapFavoriteId'] != undefined) {
      this.exitLabelEditing();
      this.loadLabels();
      this.startRefresh();
    } else if (changes['draftLabels'] != undefined && this.isDraft && this.draftLabels !== this.labels) {
      // The host replaced the draft labels (e.g. a different favorite was opened).
      this.labels = this.draftLabels;
      this.pushLabelsToEditor();
      this.refreshLabelViews();
      this.measureLabelsSoon();
      this.loadValues();
    }

    if (changes['refreshSeconds'] != undefined && changes['mapFavoriteId'] == undefined) {
      this.startRefresh();
    }

    if (changes['labelsEditable'] != undefined && !this.labelsEditable) {
      this.exitLabelEditing();
    }
  }


  ngAfterViewInit(): void {
    this.measureLabelsSoon();
    this.observeSize();
  }


  // The map's container changes size when the browser window is resized and in the resizable
  // dashboard modal. Google Maps needs to be told, and the labels (pixel positions in the map's
  // pane) need to be projected again. Watched outside Angular's zone so a drag does not run
  // change detection on every frame; one animation frame is handled at a time.
  private observeSize(): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    let lastWidth = 0;
    let lastHeight = 0;
    let frame = 0;

    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(entries => {
        const box = entries[0]?.contentRect;
        if (box == undefined || box.width === 0 || box.height === 0) {
          return;   // hidden: nothing to lay out
        }
        if (box.width === lastWidth && box.height === lastHeight) {
          return;
        }
        lastWidth = box.width;
        lastHeight = box.height;
        this.mapWidthPx = box.width;

        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.onContainerResized());
      });
      // The component's own host element is inline (a ResizeObserver reports 0x0 for those),
      // so watch the block element inside it that carries the height.
      const target = this.hostRef.nativeElement.querySelector('.map-favorite') as HTMLElement | null;
      this.resizeObserver.observe(target ?? this.hostRef.nativeElement);
    });
  }


  // Font sizes are authored for a 600px-wide (Medium) map and scale with its width.
  get fontScale(): number {
    return labelFontScale(this.mapWidthPx);
  }


  private onContainerResized(): void {
    const googleMap = this.gMap;
    if (googleMap == undefined) {
      return;
    }

    // Keep what the user is looking at in the middle of the new size.
    const center = googleMap.getCenter();
    google.maps.event.trigger(googleMap, 'resize');
    if (center != undefined) {
      googleMap.setCenter(center);
    }

    this.zone.run(() => {
      this.refreshLabelViews();
      this.measureLabelsSoon();
    });
  }


  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.exitLabelEditing();
    this.common.clearAllPinPopupLeaders();
    this.labelOverlay?.setMap(null);
    this.refreshSub?.unsubscribe();
    this.restoreToken++;
    this.destroy$.next();
    this.destroy$.complete();
  }


  // The native map: the ViewChild may not be set yet when mapInitialized fires.
  private get gMap(): google.maps.Map | undefined {
    return this.map?.googleMap ?? this.nativeMap;
  }


  onMapInitialized(map: google.maps.Map): void {
    this.nativeMap = map;
    this.mapReady = true;
    this.labelOverlay = this.createLabelOverlay();
    this.labelOverlay.setMap(map);
    this.applyPendingView();
    this.fitMapToBounds();
  }


  // ---------------------------------------------------------------- view

  // The live map state, for the host to save into a favorite / last used settings.
  getMapDetails(): MapDetails {
    const googleMap = this.gMap;
    const center = googleMap?.getCenter();
    const zoom = googleMap?.getZoom();

    const view = (center != undefined && zoom != undefined)
      ? { center: { lat: center.lat(), lng: center.lng() }, zoom, mapTypeId: googleMap.getMapTypeId() as string }
      : (this.lastKnownView ?? { center: this.initialCenter, zoom: this.initialZoom, mapTypeId: this.mapTypeId });
    this.lastKnownView = view;

    return {
      center: view.center,
      zoom: view.zoom,
      mapTypeId: view.mapTypeId,
      openPopups: this.captureOpenPopups()
    };
  }


  private applyPendingView(): void {
    const details = this.viewToApply;
    if (details == undefined || details.center == undefined) {
      return;
    }

    // Keep the bound inputs in step, so the map wrapper never re-applies stale values.
    this.initialCenter = { ...details.center };
    this.initialZoom = details.zoom;
    if (details.mapTypeId != undefined && details.mapTypeId !== '') {
      this.mapTypeId = details.mapTypeId;
    }
    this.skipNextFit = true;

    const googleMap = this.gMap;
    if (!this.mapReady || googleMap == undefined) {
      return;   // applied from onMapInitialized
    }

    googleMap.setCenter(details.center);
    googleMap.setZoom(details.zoom);
    if (details.mapTypeId != undefined && details.mapTypeId !== '') {
      googleMap.setMapTypeId(details.mapTypeId);
    }
    this.viewToApply = null;
  }


  // Zoom/center the map so all pins are visible. Skipped once after a saved view was
  // restored, and when the pins did not change (so changing only the reading types
  // does not move the map the user is looking at).
  private fitMapToBounds(): void {
    if (this.skipNextFit) {
      this.skipNextFit = false;
      return;
    }

    // Defer so the markers have been bound.
    setTimeout(() => {
      const googleMap = this.gMap;
      if (googleMap == undefined || this.geoMapItems.length === 0) {
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      this.geoMapItems.forEach(item => bounds.extend(new google.maps.LatLng(item.gpsLat, item.gpsLong)));

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      if (ne.lat() === sw.lat() && ne.lng() === sw.lng()) {
        googleMap.setCenter(bounds.getCenter());
        googleMap.setZoom(15);
      } else {
        googleMap.fitBounds(bounds);
      }
    });
  }


  onMapIdle(): void {
    this.emitDetails();
    this.measureLabelsSoon();
  }


  private emitDetails(): void {
    if (this.restoring || !this.mapReady) {
      return;
    }
    this.mapDetailsChanged.emit(this.getMapDetails());
  }


  // ---------------------------------------------------------------- pins

  private pinKey(uuid: string, lat: number, lng: number): string {
    return `${uuid}|${lat.toFixed(6)}|${lng.toFixed(6)}`;
  }


  // Build one pin per sensor and position from the readings, then bring back the open
  // popups: from the saved details when a favorite / last used state was just applied,
  // otherwise from what is open right now (the pins are rebuilt from scratch).
  private rebuildPins(): void {
    const popupsToRestore: MapPopupState[] = (this.pendingDetails != null)
      ? (this.pendingDetails.openPopups ?? [])
      : this.captureOpenPopups();

    this.restoreToken++;
    this.common.clearAllPinPopupLeaders();   // the lines live on Google's map, not in Angular's DOM

    const sensorByUuid = new Map<string, Sensor>();
    (this.sensors ?? []).forEach(s => sensorByUuid.set(s.uuid, s));

    const groups = new Map<string, { sensor: Sensor; gps: SensorReadingInfo['gpsLocation']; source: SensorReadingInfo['gpsSource']; readings: SensorReadingInfo[] }>();

    (this.sensorReadings ?? []).forEach(reading => {
      const sensor = sensorByUuid.get(reading.uuid);
      const gps = reading.gpsLocation;
      if (sensor == undefined || gps == undefined || reading.gpsSource === 'none') {
        return;
      }
      if (Math.abs(gps.gpsLat) === 0 && Math.abs(gps.gpsLong) === 0) {
        return;
      }

      const key = this.pinKey(reading.uuid, gps.gpsLat, gps.gpsLong);
      let group = groups.get(key);
      if (group == undefined) {
        group = { sensor, gps, source: reading.gpsSource, readings: [] };
        groups.set(key, group);
      }
      group.readings.push(reading);
    });

    const items: GeoMapItem[] = [];
    groups.forEach((group, key) => {
      const source = group.source ?? 'reading';
      const newest = group.readings.reduce((a, b) => new Date(b.createdDate).getTime() > new Date(a.createdDate).getTime() ? b : a);

      items.push({
        key,
        sensorUuid: group.sensor.uuid,
        gpsLong: group.gps.gpsLong,
        gpsLat: group.gps.gpsLat,
        gpsAlt: group.gps.gpsAlt,
        description: group.gps.locationDescription,
        sensorRef: this.common.getSensorDisplayName(group.sensor),
        labelOptions: { color: 'black', fontFamily: '', fontSize: '14px', fontWeight: 'bold', text: ' ' },
        iconUrl: '',
        icon: this.common.gpsPinIcon(source),
        headerColors: this.common.gpsPinHeaderColors(source),
        readingDate: new Date(newest.createdDate),
        readings: this.buildReadingsDictionary(group.readings, group.gps),
        readingTypeCharted: '',
        isOpen: false,
        selectedReadingTypes: [],
        charts: []
      });
    });

    this.geoMapItems = items;

    // Fit only when the set of pins changed.
    const signature = items.map(i => i.key).sort().join(';');
    const pinsChanged = signature !== this.lastPinSignature;
    this.lastPinSignature = signature;
    if (this.mapReady && items.length > 0) {
      if (pinsChanged) {
        this.fitMapToBounds();   // consumes skipNextFit when a saved view was just applied
      } else {
        this.skipNextFit = false;
      }
    }

    if (popupsToRestore.length > 0 && items.length > 0) {
      this.restoring = true;
      this.restorePopups(popupsToRestore, this.restoreToken, 0);
    } else {
      this.restoring = false;
      if (items.length > 0) {
        this.pendingDetails = null;   // nothing to restore, so the saved details are consumed
      }
    }
  }


  // The newest value of each reading type at this pin, plus its coordinates.
  private buildReadingsDictionary(readings: SensorReadingInfo[], gps: SensorReadingInfo['gpsLocation']): { [key: string]: string } {
    const newestByType = new Map<string, SensorReadingInfo>();

    readings.forEach(reading => {
      if (reading.type == null || reading.value == null) {
        return;
      }
      const current = newestByType.get(reading.type);
      if (current == undefined || new Date(reading.createdDate).getTime() > new Date(current.createdDate).getTime()) {
        newestByType.set(reading.type, reading);
      }
    });

    const dictionary: { [key: string]: string } = {};
    newestByType.forEach((reading, type) => dictionary[type] = Number(reading.value).toPrecision(9));

    if (gps != null) {
      dictionary['gpsLat'] = gps.gpsLat.toString();
      dictionary['gpsLong'] = gps.gpsLong.toString();
    }
    return dictionary;
  }


  // ---------------------------------------------------------------- popups

  private captureOpenPopups(): MapPopupState[] {
    return this.geoMapItems
      .filter(item => item.isOpen)
      .map(item => ({
        sensorUuid: item.sensorUuid,
        gpsLat: item.gpsLat,
        gpsLong: item.gpsLong,
        dragOffset: { x: item.dragOffset?.x ?? 0, y: item.dragOffset?.y ?? 0 },
        selectedReadingTypes: [...(item.selectedReadingTypes ?? [])]
      }));
  }


  // Reopen saved popups once the markers and info windows have been created by the map
  // wrapper (that happens asynchronously, so this polls briefly).
  private restorePopups(states: MapPopupState[], token: number, attempt: number): void {
    if (token !== this.restoreToken) {
      return;   // superseded by a newer rebuild
    }

    const markers = this.markers?.toArray() ?? [];
    const windows = this.infoWindows?.toArray() ?? [];
    const googleMap = this.gMap;
    const ready = googleMap != undefined
      && markers.length === this.geoMapItems.length
      && windows.length === this.geoMapItems.length
      && markers.every(m => m.marker != undefined)
      && windows.every(w => w.infoWindow != undefined);

    if (!ready) {
      if (attempt < 100) {
        setTimeout(() => this.restorePopups(states, token, attempt + 1), 100);
      } else {
        this.restoring = false;
      }
      return;
    }

    states.forEach(state => {
      const key = this.pinKey(state.sensorUuid, state.gpsLat, state.gpsLong);
      const index = this.geoMapItems.findIndex(item => item.key === key);
      if (index < 0) {
        return;   // the pin is no longer on the map
      }

      const item = this.geoMapItems[index];
      item.selectedReadingTypes = (state.selectedReadingTypes ?? [])
        .filter(type => item.readings[type] != undefined && this.isChartableReadingType(type));
      this.buildCharts(item);
      this.common.restorePinPopup(windows[index], markers[index], item, state.dragOffset, googleMap);
    });

    this.pendingDetails = null;

    // Let the popups settle before the host hears about the (unchanged) state.
    setTimeout(() => {
      if (token === this.restoreToken) {
        this.restoring = false;
      }
    }, 1500);
  }


  openPopup(infoWindow: MapInfoWindow, marker: MapMarker, item: GeoMapItem): void {
    this.common.openPinPopup(infoWindow, marker, item);
    this.emitDetails();
  }


  onPopupClosed(item: GeoMapItem): void {
    item.isOpen = false;
    this.common.hidePinPopupLeader(item);
    this.emitDetails();
  }


  onPopupDragEnded(item: GeoMapItem, event: any): void {
    this.common.updatePinPopupLeader(item, event.source.getFreeDragPosition(), this.gMap, event.source.getRootElement());
    this.emitDetails();
  }


  isReadingTypeSelected(item: GeoMapItem, readingType: string): boolean {
    return item.selectedReadingTypes != undefined && item.selectedReadingTypes.includes(readingType);
  }


  // The pin's position is listed with its readings but is not a reading type with a history,
  // so there is nothing to chart for it.
  isChartableReadingType(readingType: string): boolean {
    return readingType !== 'gpsLat' && readingType !== 'gpsLong';
  }


  // A click on a reading type turns its chart on or off. The charts of a popup stack top to
  // bottom in the order they were turned on.
  toggleReadingType(event: Event, item: GeoMapItem, readingType: string): void {
    if (!this.isChartableReadingType(readingType)) {
      return;
    }

    const selected = item.selectedReadingTypes ?? [];
    item.selectedReadingTypes = selected.includes(readingType)
      ? selected.filter(type => type !== readingType)
      : [...selected, readingType];

    this.buildCharts(item);
    this.emitDetails();

    // A popup that was dragged away from its pin grows downward when a chart is added, which
    // moves the bottom edge its leader line ends on. That line is only measured at the end of a
    // drag, so measure it again once the new charts have been laid out. (A popup that was
    // never dragged keeps its bottom edge on the pin, so it needs nothing.)
    const popupEl = (event.target as HTMLElement | null)?.closest?.('.gm-style-iw-c') as HTMLElement | null | undefined;
    if (popupEl != undefined && (item.dragOffset?.x || item.dragOffset?.y)) {
      setTimeout(() => this.common.updatePinPopupLeader(item, item.dragOffset, this.gMap, popupEl), 100);
    }
  }


  private buildCharts(item: GeoMapItem): void {
    item.charts = (item.selectedReadingTypes ?? [])
      .map(readingType => ({ readingType, data: this.chartData(item, readingType) }))
      .filter(chart => chart.data.length > 0);
  }


  private chartData(item: GeoMapItem, readingType: string): any[] {
    const series = (this.sensorReadings ?? [])
      .filter(r => r.uuid === item.sensorUuid && r.type === readingType && r.value != 'nan' && r.value != 'NaN')
      .map(r => ({ name: new Date(r.createdDate), value: Number(r.value) }))
      .sort((a, b) => a.name.getTime() - b.name.getTime());

    return series.length === 0 ? [] : [{ name: readingType + ', ' + item.sensorRef, series, uuid: item.sensorUuid }];
  }


  trackByKey(_index: number, item: GeoMapItem): string {
    return item.key;
  }


  trackByReadingType(_index: number, chart: { readingType: string }): string {
    return chart.readingType;
  }


  // ---------------------------------------------------------------- labels: loading

  private loadLabels(): void {
    this.labels = [];
    this.labelValues.clear();
    this.boxSizes.clear();

    if (this.isDraft) {
      this.labels = this.draftLabels;
      this.pushLabelsToEditor();
      this.refreshLabelViews();
      this.measureLabelsSoon();
      this.loadValues();
      return;
    }

    this.refreshLabelViews();

    if (!(this.mapFavoriteId > 0)) {
      return;
    }

    const id = this.mapFavoriteId;
    this.mapFavoriteService.getMapLabels(id).pipe(takeUntil(this.destroy$)).subscribe(
      (results: MapLabelInfo[] | null) => {
        if (this.mapFavoriteId !== id) {
          return;
        }
        this.labels = results ?? [];
        this.pushLabelsToEditor();
        this.refreshLabelViews();
        this.measureLabelsSoon();
        this.loadValues();
      },
      _err => this.toastr.error('Unable to load map labels'));
  }


  private loadValues(): void {
    const dynamicLabels = this.labels.filter(l => l.isDynamic);
    if (!this.hasLabelSource || dynamicLabels.length === 0) {
      return;
    }

    const id = this.mapFavoriteId;
    const draft = this.isDraft;
    const values$ = draft
      ? this.mapFavoriteService.getPreviewLabelValues(dynamicLabels)
      : this.mapFavoriteService.getLabelValues(id);

    values$.pipe(takeUntil(this.destroy$)).subscribe(
      (results: ImageLabelValue[] | null) => {
        if (!draft && this.mapFavoriteId !== id) {
          return;
        }
        this.labelValues = new Map((results ?? []).map(v => [v.labelId, v]));
        this.refreshLabelViews();
        this.measureLabelsSoon();
      },
      _err => { });
  }


  // Periodic refresh of the dynamic values; skipped mid-drag.
  private startRefresh(): void {
    this.refreshSub?.unsubscribe();
    this.refreshSub = null;
    if (!this.hasLabelSource || !(this.refreshSeconds > 0)) {
      return;
    }
    const period = this.refreshSeconds * 1000;
    this.refreshSub = timer(period, period).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.labelDrag == null) {
        this.loadValues();
      }
    });
  }


  // ---------------------------------------------------------------- labels: rendering

  // Overlay whose only job is to host the label layer in the map's pane (so labels pan
  // and zoom with the map) and to re-project the labels whenever Google redraws it.
  private createLabelOverlay(): any {
    const overlay: any = new google.maps.OverlayView();
    const layer = this.labelLayerRef.nativeElement;

    overlay.onAdd = function () {
      this.getPanes().overlayMouseTarget.appendChild(layer);
    };

    overlay.onRemove = function () {
      layer.remove();
    };

    overlay.draw = () => {
      this.zone.run(() => {
        this.refreshLabelViews();
        this.measureLabelsSoon();
      });
    };

    return overlay;
  }


  private toDiv(lat: number, lng: number): { x: number; y: number } | null {
    const projection = this.labelOverlay?.getProjection?.();
    if (projection == undefined || lat == undefined || lng == undefined) {
      return null;
    }
    const point = projection.fromLatLngToDivPixel(new google.maps.LatLng(lat, lng));
    return point != undefined ? { x: point.x, y: point.y } : null;
  }


  private fromDiv(x: number, y: number): { lat: number; lng: number } | null {
    const projection = this.labelOverlay?.getProjection?.();
    if (projection == undefined) {
      return null;
    }
    const latLng = projection.fromDivPixelToLatLng(new google.maps.Point(x, y));
    return latLng != undefined ? { lat: latLng.lat(), lng: latLng.lng() } : null;
  }


  private propsOf(label: MapLabelInfo): ImageLabelProperties {
    return parseLabelProps(label);
  }


  // Re-project every label to pixels and rebuild what the template renders.
  private refreshLabelViews(): void {
    const views: LabelView[] = [];
    const arrows: LabelArrow[] = [];

    this.labels.forEach(label => {
      const anchor = this.toDiv(label.anchorLat, label.anchorLng);
      if (anchor == null) {
        return;
      }

      const props = this.propsOf(label);
      const scale = this.fontScale;
      const style = labelBoxStyle(label, props, scale);
      const hasArrow = labelTypeHasArrow(label.labelType);
      const isRegion = label.labelType === ImageLabelType.Region;
      let left = anchor.x;
      let top = anchor.y;

      if (isRegion) {
        const se = this.toDiv(label.lat2, label.lng2) ?? { x: anchor.x + 150, y: anchor.y + 100 };
        left = Math.min(anchor.x, se.x);
        top = Math.min(anchor.y, se.y);
        style['width.px'] = Math.abs(se.x - anchor.x);
        style['height.px'] = Math.abs(se.y - anchor.y);
      } else if (hasArrow) {
        // The stored offset is for a 600px-wide map; it grows with the font so a bigger box does not cover its pin.
        left = anchor.x + (label.offsetX ?? -120) * scale;
        top = anchor.y + (label.offsetY ?? -60) * scale;
      }

      if (!isRegion && label.boxWidth != undefined) {
        style['width.px'] = label.boxWidth * scale;
      }
      style['left.px'] = left;
      style['top.px'] = top;

      const showsArrow = hasArrow && props.showArrow === true;
      if (showsArrow) {
        const size = this.boxSizes.get(label.id) ?? { w: 0, h: 0 };
        arrows.push({
          x1: Math.min(Math.max(anchor.x, left), left + size.w),
          y1: Math.min(Math.max(anchor.y, top), top + size.h),
          x2: anchor.x,
          y2: anchor.y,
          color: arrowColor(props)
        });
      }

      const value = this.labelValues.get(label.id);
      views.push({
        label,
        style,
        valueStyle: labelValueStyle(props, scale),
        valueText: formatLabelValue(props, value),
        valueTooltip: labelValueTooltip(value),
        isRegion,
        isPlain: label.labelType === ImageLabelType.TextArrow && !props.hasStickyBackground && !label.isDynamic,
        target: hasArrow ? anchor : null
      });
    });

    this.labelViews = views;
    this.labelArrows = arrows;
  }


  trackByLabelView(_index: number, view: LabelView): number {
    return view.label.id;
  }


  trackByIndex(index: number): number {
    return index;
  }


  // Box sizes are only known once rendered; arrows start at the box edge, so re-project
  // when a size changes.
  measureLabelsSoon(): void {
    setTimeout(() => this.measureLabels());
  }


  private measureLabels(): void {
    const layer = this.labelLayerRef?.nativeElement;
    if (layer == undefined) {
      return;
    }

    let changed = false;
    layer.querySelectorAll<HTMLElement>('[data-label-id]').forEach(el => {
      const id = Number(el.dataset['labelId']);
      const current = this.boxSizes.get(id);
      if (current == undefined || current.w !== el.offsetWidth || current.h !== el.offsetHeight) {
        this.boxSizes.set(id, { w: el.offsetWidth, h: el.offsetHeight });
        changed = true;
      }
    });

    if (changed) {
      this.refreshLabelViews();
    }
  }


  // ---------------------------------------------------------------- labels: editing

  toggleLabelEditing(): void {
    if (this.labelsEditMode) {
      this.exitLabelEditing();
    } else {
      this.enterLabelEditing();
    }
  }


  private enterLabelEditing(): void {
    if (!this.labelsEditable || !this.hasLabelSource) {
      return;
    }
    this.labelsEditMode = true;
    this.labelsEditModeChange.emit(true);

    this.labelEditorRef = this.modalService.show(ImageLabelEditorModalComponent, {
      animated: false,
      backdrop: false,
      ignoreBackdropClick: true,
      class: 'image-label-editor-dialog',
      initialState: { labels: this.labels }
    });
    const content = this.labelEditorRef.content as ImageLabelEditorModalComponent;
    content.onAdd.subscribe((type: number) => this.addLabel(type));
    content.onUpdate.subscribe((label: any) => this.persistLabel(label as MapLabelInfo, true));
    content.onDelete.subscribe((id: number) => this.deleteLabel(id));
    content.onClosed.subscribe(() => this.exitLabelEditing());
  }


  private exitLabelEditing(): void {
    if (!this.labelsEditMode) {
      return;
    }
    this.labelsEditMode = false;
    this.endLabelDrag(false);
    if (this.labelEditorRef != undefined) {
      this.labelEditorRef.hide();
      this.labelEditorRef = null;
    }
    this.labelsEditModeChange.emit(false);
  }


  private pushLabelsToEditor(selectId?: number): void {
    if (this.labelEditorRef == undefined || this.labelEditorRef.content == undefined) {
      return;
    }
    const content = this.labelEditorRef.content as ImageLabelEditorModalComponent;
    content.labels = this.labels;
    if (selectId != undefined) {
      content.selectById(selectId);
    }
  }


  // A new label of the given type at the middle of the visible map. Arrow labels get
  // their box up-left of the tip so the arrow is visible; a region covers ~20% of the view.
  private addLabel(type: number): void {
    const googleMap = this.gMap;
    if (!this.hasLabelSource || googleMap == undefined) {
      return;
    }

    const center = googleMap.getCenter();
    const bounds = googleMap.getBounds();
    const latSpan = bounds != undefined ? bounds.getNorthEast().lat() - bounds.getSouthWest().lat() : 0.02;
    const lngSpan = bounds != undefined ? bounds.getNorthEast().lng() - bounds.getSouthWest().lng() : 0.02;

    const request: MapLabelInfo = {
      id: 0,
      mapFavoriteID: this.mapFavoriteId,
      labelType: type,
      text: type === ImageLabelType.Region ? '' : 'New label',
      anchorLat: center.lat(),
      anchorLng: center.lng(),
      offsetX: null,
      offsetY: null,
      lat2: null,
      lng2: null,
      boxWidth: null,
      properties: JSON.stringify(newLabelProps(type)),
      isDynamic: false,
      sensorID: null,
      sensorReadingTypeID: null,
      aggregationType: null,
      aggregationMinutes: null
    };

    if (type === ImageLabelType.Region) {
      request.anchorLat = center.lat() + latSpan * 0.1;
      request.anchorLng = center.lng() - lngSpan * 0.1;
      request.lat2 = center.lat() - latSpan * 0.1;
      request.lng2 = center.lng() + lngSpan * 0.1;
    }
    this.ensureGeometryForType(request);

    if (this.isDraft) {
      const tempId = Math.min(0, ...this.labels.map(l => l.id)) - 1;
      this.labels = [...this.labels, { ...request, id: tempId }];
      this.draftLabelsChange.emit(this.labels);
      this.pushLabelsToEditor(tempId);
      this.refreshLabelViews();
      this.measureLabelsSoon();
      return;
    }

    this.mapFavoriteService.createMapLabel(request).subscribe(
      (newId: any) => {
        const id = Number(newId);
        if (isNaN(id) || id <= 0) {
          this.toastr.error('Unable to add label');
          return;
        }
        this.labels = [...this.labels, { ...request, id }];
        this.pushLabelsToEditor(id);
        this.refreshLabelViews();
        this.measureLabelsSoon();
      },
      _err => this.toastr.error('Unable to add label'));
  }


  // Persist a label's current state. Editor changes (reload true) may have made the
  // label dynamic or changed its sensor, so its value is refetched.
  private persistLabel(label: MapLabelInfo, reload: boolean): void {
    if (label == undefined || (!this.isDraft && label.id <= 0)) {
      return;
    }
    if (reload) {
      this.ensureGeometryForType(label);
    }

    // Draft labels are edited in place; the host saves them later.
    if (this.isDraft) {
      this.draftLabelsChange.emit(this.labels);
      if (reload && label.isDynamic) {
        this.loadValues();
      }
      this.refreshLabelViews();
      this.measureLabelsSoon();
      return;
    }

    this.mapFavoriteService.updateMapLabel(label, this.username).subscribe(
      () => {
        if (reload && label.isDynamic) {
          this.loadValues();
        }
      },
      _err => this.toastr.error('Unable to save label'));

    this.refreshLabelViews();
    this.measureLabelsSoon();
  }


  // Seed any geometry the label's current type needs but is missing, e.g. after the
  // user switches its type in the editor.
  private ensureGeometryForType(label: MapLabelInfo): void {
    if (labelTypeHasArrow(label.labelType) && (label.offsetX == undefined || label.offsetY == undefined)) {
      label.offsetX = -120;
      label.offsetY = -60;
    }

    if (label.labelType === ImageLabelType.Region && (label.lat2 == undefined || label.lng2 == undefined)) {
      const anchor = this.toDiv(label.anchorLat, label.anchorLng);
      const se = anchor != null ? this.fromDiv(anchor.x + 150, anchor.y + 100) : null;
      label.lat2 = se?.lat ?? label.anchorLat - 0.01;
      label.lng2 = se?.lng ?? label.anchorLng + 0.01;
    }
  }


  private deleteLabel(id: number): void {
    if (!this.isDraft && id > 0) {
      this.mapFavoriteService.deleteMapLabel(id, this.username).subscribe(
        () => { },
        _err => this.toastr.error('Unable to delete label'));
    }

    this.labels = this.labels.filter(l => l.id !== id);
    if (this.isDraft) {
      this.draftLabelsChange.emit(this.labels);
    }
    this.labelValues.delete(id);
    this.boxSizes.delete(id);
    this.pushLabelsToEditor();
    this.refreshLabelViews();
  }


  // ---------------------------------------------------------------- labels: dragging

  startLabelDrag(event: PointerEvent, label: MapLabelInfo, mode: 'box' | 'target' | 'resize'): void {
    if (!this.labelsEditMode || event.button !== 0) {
      return;
    }
    const anchor = this.toDiv(label.anchorLat, label.anchorLng);
    if (anchor == null) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.labelDrag = {
      label,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      anchor,
      se: label.lat2 != undefined ? this.toDiv(label.lat2, label.lng2) : null,
      offsetX: label.offsetX ?? 0,
      offsetY: label.offsetY ?? 0,
      moved: false
    };

    // The map must not pan while a label is being moved.
    this.gMap?.setOptions({ draggable: false });

    // Clicking a label on the map also selects it in the editor.
    (this.labelEditorRef?.content as ImageLabelEditorModalComponent)?.selectById(label.id);
  }


  @HostListener('window:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    const drag = this.labelDrag;
    if (drag == null) {
      return;
    }

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    const label = drag.label;
    drag.moved = true;

    // The pointer moves in screen pixels, but offsets are stored for a 600px-wide map (see refreshLabelViews).
    const scale = this.fontScale;

    if (drag.mode === 'box') {
      if (labelTypeHasArrow(label.labelType)) {
        label.offsetX = Math.round(drag.offsetX + dx / scale);
        label.offsetY = Math.round(drag.offsetY + dy / scale);
      } else {
        const moved = this.fromDiv(drag.anchor.x + dx, drag.anchor.y + dy);
        if (moved != null) {
          label.anchorLat = moved.lat;
          label.anchorLng = moved.lng;
        }
        if (label.labelType === ImageLabelType.Region && drag.se != null) {
          const movedSe = this.fromDiv(drag.se.x + dx, drag.se.y + dy);
          if (movedSe != null) {
            label.lat2 = movedSe.lat;
            label.lng2 = movedSe.lng;
          }
        }
      }
    } else if (drag.mode === 'target') {
      // Move the arrow tip; the box stays where it is on screen.
      const moved = this.fromDiv(drag.anchor.x + dx, drag.anchor.y + dy);
      if (moved != null) {
        label.anchorLat = moved.lat;
        label.anchorLng = moved.lng;
        label.offsetX = Math.round(drag.offsetX - dx / scale);
        label.offsetY = Math.round(drag.offsetY - dy / scale);
      }
    } else if (drag.se != null) {
      const moved = this.fromDiv(Math.max(drag.se.x + dx, drag.anchor.x + 10), Math.max(drag.se.y + dy, drag.anchor.y + 10));
      if (moved != null) {
        label.lat2 = moved.lat;
        label.lng2 = moved.lng;
      }
    }

    this.refreshLabelViews();
  }


  @HostListener('window:pointerup')
  onPointerUp(): void {
    this.endLabelDrag(true);
  }


  private endLabelDrag(save: boolean): void {
    const drag = this.labelDrag;
    if (drag == null) {
      return;
    }
    this.labelDrag = null;
    this.gMap?.setOptions({ draggable: true });

    if (save && drag.moved) {
      this.persistLabel(drag.label, false);
    }
  }
}
